import React from 'react';
import { IEquipmentCategory } from './EquipmentCategoryGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { roundTo } from '../../helpers/roundTo';
import { IGasType } from './EquipmentDetail';
import GasTypeBadge from './GasTypeBadge';
import { formatUnit } from '../../helpers/formatUnit';

interface IEquipmentMetric {
    label: string;
    value: string;
    unit: number;
    gasType?: IGasType;
}

interface IEquipmentDevice {
    id: string;
    name: string;
    serialNumber?: string;
    category: string;
    imageUrl: string;
    building?: string;
    floor?: string;
    room?: string;
    metric?: IEquipmentMetric;
    alarmCount?: number;
    hasAlert: boolean;
}

interface EquipmentCardProps {
    device: IEquipmentDevice;
    onClick: (deviceId: string) => void;
    isStacked?: boolean;
    stackCount?: number;
    category: IEquipmentCategory;
}

const EquipmentCard: React.FC<EquipmentCardProps> = ({
    device,
    onClick,
    isStacked = false,
    stackCount = 0,
    category
}) => {
    const locationParts = [device.building, device.floor, device.room].filter(
        Boolean
    );
    const locationText = locationParts.join(', ');

    return (
        <div
            className={`equipment-card ${device.hasAlert ? 'has-alert' : ''} ${
                isStacked ? 'stacked' : ''
            }`}
            onClick={() => onClick(device.id)}
            role="button"
            tabIndex={0}
            onKeyPress={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    onClick(device.id);
                }
            }}
            aria-label={`Equipment: ${device.name}, ${
                device.hasAlert
                    ? `${device.alarmCount || 1} alarm${
                          device.alarmCount !== 1 ? 's' : ''
                      }`
                    : 'no alarms'
            }`}
        >
            {device.hasAlert && (
                <div
                    className="alarm-badge alt-badge"
                    aria-label={
                        device.alarmCount && device.alarmCount > 0
                            ? `${device.alarmCount} alarms`
                            : '1 alarm'
                    }
                >
                    !
                    {device.alarmCount && device.alarmCount > 0 ? (
                        <span className="alarm-count">{device.alarmCount}</span>
                    ) : null}
                </div>
            )}

            <div className="card-image-container">
                <img
                    src={device.imageUrl}
                    alt={device.name}
                    className="equipment-image"
                    onError={e => {
                        // Fallback to placeholder if image fails to load
                        (e.target as HTMLImageElement).src =
                            '/equipment-tile.png';
                    }}
                />
            </div>

            <div className="card-content">
                {!isStacked && (
                    <div className="equipment-name-location-container">
                        <h4 className="equipment-name">{device.name}</h4>
                        {device.serialNumber && (
                            <p className="equipment-serial">
                                <strong>Serial:</strong> {device.serialNumber}
                            </p>
                        )}
                        {locationText && (
                            <p className="equipment-location">{locationText}</p>
                        )}
                    </div>
                )}

                {!isStacked && device.metric && (
                    <div className="metric-container">
                        <div
                            className="metric-label"
                            title={device.metric.label}
                        >
                            {device.metric.label}
                        </div>
                        <div className="metric-value-container">
                            <span className="metric-value">
                                {roundTo(Number(device.metric.value), 1)}
                                <span className="metric-unit">
                                    {formatUnit(device.metric.unit)}
                                </span>
                            </span>
                        </div>
                        {device.metric.gasType && (
                            <div className="gas-type-bar-container">
                                <GasTypeBadge gasType={device.metric.gasType} />
                            </div>
                        )}
                    </div>
                )}
                {isStacked && stackCount > 0 && (
                    <button className="category-header">
                        <div className="category-title-container">
                            <h3>{category.name}</h3>
                            <span className="category-device-count">
                                {category.devices.length}
                            </span>
                        </div>
                        <div className="expand-card-view">
                            Expand card view{' '}
                            <FontAwesomeIcon
                                icon={['far', 'chevron-double-right']}
                            />
                        </div>
                    </button>
                )}
            </div>
        </div>
    );
};

export default EquipmentCard;
